import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

const automationItems = [
  {
    title: 'Продажи',
    content: `• Вход из рекламы или сайта, 2–4 уточняющих вопроса
• Формирование тёплой заявки с контактами
• Автоматическая отправка КП или запрос предоплаты
• Фиксация UTM-меток и источника трафика
• SLA реакции менеджера на новую заявку`,
    faq: [
      { q: 'Как быстро бот обрабатывает заявку?', a: 'Моментально - клиент получает ответ в течение секунд' },
      { q: 'Можно ли настроить разные сценарии для разных продуктов?', a: 'Да, создаём гибкие воронки под каждое направление' }
    ]
  },
  {
    title: 'Маркетинг',
    content: `• Сегментация аудитории для таргетированных рассылок
• Промокоды и реферальные программы
• A/B-тестирование офферов без привлечения разработчиков
• Автоматические триггерные рассылки
• Аналитика эффективности кампаний`,
    faq: [
      { q: 'Можно ли интегрировать с рекламными кабинетами?', a: 'Да, передаём события и конверсии в VK, Яндекс.Директ и другие площадки' }
    ]
  },
  {
    title: 'Поддержка клиентов',
    content: `• База знаний с автоматическими ответами на FAQ
• Эскалация сложных вопросов к операторам
• Оценка качества обслуживания (CSAT)
• История обращений каждого клиента
• Автоматические уведомления о статусе заявки`,
    faq: [
      { q: 'Как бот определяет, когда передать диалог оператору?', a: 'По ключевым словам, сложности запроса или по команде клиента' }
    ]
  },
  {
    title: 'Операционные задачи',
    content: `• Онлайн-запись и бронирование услуг
• Автоматические напоминания о встречах
• Интеграция с календарём и складом
• Управление расписанием в реальном времени
• Уведомления об изменениях`,
    faq: []
  },
  {
    title: 'Платежи и контроль',
    content: `• Автоматическое отслеживание статусов оплат
• Генерация и отправка чеков
• Система ролей и прав доступа
• Журнал всех действий пользователей
• Отчётность по периодам`,
    faq: []
  },
  {
    title: 'Безопасность и доступы',
    content: `• Разграничение ролей и прав доступа
• Единый вход через корпоративные системы
• Защита вебхуков и API
• Логирование всех событий
• Регулярные бэкапы данных
• Уведомления о подозрительной активности`,
    faq: []
  },
  {
    title: 'Отчётность и мониторинг',
    content: `• Единый дашборд: лиды, конверсия, оплаты, среднее время обработки
• Ежедневные выгрузки в email или Google Sheets
• Экспорт данных в CSV/Excel
• Оповещения при просадках ключевых метрик
• Кастомные отчёты под ваши KPI`,
    faq: []
  },
  {
    title: 'Согласования',
    content: `• Настройка цепочек согласований
• Кнопки «Одобрить/Отклонить» с комментариями
• Журнал всех согласований
• Автоматические напоминания ответственным
• Эскалация при просрочке`,
    faq: []
  },
  {
    title: 'Интеграции с сайтом и рекламой',
    content: `• Передача UTM-меток из рекламы
• События: просмотр, клик, заявка, оплата
• Настройка ретаргетинга по действиям в боте
• Постбеки в рекламные системы
• Сверка данных между системами`,
    faq: []
  },
  {
    title: 'Логистика и доставка',
    content: `• Отслеживание статусов заказа
• Трекинг посылок
• Выбор окон доставки
• Автоматические напоминания курьерам
• Интеграция с курьерскими службами
• История всех заказов клиента`,
    faq: []
  },
  {
    title: 'И много другое',
    content: `• Универсальность платформы Telegram
• Масштабирование под любые бизнес-процессы
• Внедрение без остановки текущей работы
• Обучение команды и техподдержка
• Регулярные обновления функционала`,
    faq: []
  }
]

export default function AutomationSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Что автоматизируем
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Полный спектр бизнес-процессов от продаж до логистики
          </p>
        </motion.div>

        <div className="space-y-4">
          {automationItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden hover:border-blue-500 transition-colors"
            >
              <button
                onClick={() => toggleAccordion(index)}
                onKeyDown={(e) => e.key === 'Enter' && toggleAccordion(index)}
                className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                aria-expanded={openIndex === index}
              >
                <span className="text-xl font-bold text-gray-900">
                  {String(index + 1).padStart(2, '0')}. {item.title}
                </span>
                <ChevronDown
                  className={`w-6 h-6 text-gray-500 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6 pt-2">
                      <div className="prose prose-gray max-w-none">
                        {item.content.split('\n').map((line, i) => (
                          <p key={i} className="text-gray-600 mb-2">
                            {line}
                          </p>
                        ))}
                      </div>

                      {item.faq.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">
                            Частые вопросы:
                          </h4>
                          {item.faq.map((faqItem, faqIndex) => (
                            <div key={faqIndex} className="mb-3">
                              <p className="text-sm font-medium text-gray-700 mb-1">
                                {faqItem.q}
                              </p>
                              <p className="text-sm text-gray-600">
                                {faqItem.a}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
